import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

const initials = (value = '') => {
  const parts = String(value).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'A';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || 'A';
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const preview = (text = '') => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length > 90 ? `${clean.slice(0, 90)}...` : clean;
};

const MessageBubble = ({ message }) => {
  const fromParent = message.senderType === 'parent';
  return (
    <Stack direction={fromParent ? 'row' : 'row-reverse'} spacing={1.5} alignItems="flex-end">
      <Avatar sx={{ bgcolor: fromParent ? 'secondary.main' : 'primary.main' }}>
        {initials(message.sender?.name)}
      </Avatar>
      <Paper
        elevation={0}
        sx={{
          maxWidth: '78%',
          px: 2,
          py: 1.25,
          borderRadius: 3,
          borderBottomLeftRadius: fromParent ? 6 : 18,
          borderBottomRightRadius: fromParent ? 18 : 6,
          bgcolor: fromParent ? 'background.paper' : 'primary.light',
          border: '1px solid',
          borderColor: fromParent ? 'divider' : 'primary.light',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
          {fromParent ? 'Parent' : message.sender?.role || 'Tutor'} · {message.sender?.name || 'User'}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.5 }}>
          {message.content}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.75 }}>
          {formatDateTime(message.createdAt)}
        </Typography>
      </Paper>
    </Stack>
  );
};

const MessageOversight = () => {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [note, setNote] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [savingFlag, setSavingFlag] = useState(false);
  const [error, setError] = useState('');

  const loadConversations = useCallback(async () => {
    setLoadingList(true);
    setError('');
    try {
      const { data } = await api.get('/booking-v1/admin/messages/conversations', {
        params: { search: search.trim() || undefined, flagged: flaggedOnly || undefined, limit: 100 },
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setConversations(items);
      setSelected((prev) => {
        if (prev && items.some((item) => item.conversationKey === prev.conversationKey)) {
          return items.find((item) => item.conversationKey === prev.conversationKey);
        }
        return items[0] || null;
      });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load message oversight.');
      setConversations([]);
    } finally {
      setLoadingList(false);
    }
  }, [search, flaggedOnly]);

  const loadConversation = useCallback(async (conversation) => {
    if (!conversation?.conversationKey) {
      setMessages([]);
      return;
    }
    setLoadingMessages(true);
    setError('');
    try {
      const { data } = await api.get(`/booking-v1/admin/messages/conversations/${encodeURIComponent(conversation.conversationKey)}`, {
        params: { limit: 100 },
      });
      setMessages([...(Array.isArray(data?.items) ? data.items : [])].reverse());
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load conversation.');
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    setNote(selected?.adminNote || '');
    if (selected) loadConversation(selected);
  }, [selected, loadConversation]);

  const filteredConversations = useMemo(() => conversations, [conversations]);

  const saveFlag = async (flagged) => {
    if (!selected?.conversationKey) return;
    setSavingFlag(true);
    setError('');
    try {
      const { data } = await api.patch(
        `/booking-v1/admin/messages/conversations/${encodeURIComponent(selected.conversationKey)}/flag`,
        { flagged, note }
      );
      const next = { ...selected, flagged: data.flagged, adminNote: data.adminNote };
      setSelected(next);
      setConversations((prev) => prev.map((item) => (
        item.conversationKey === next.conversationKey ? { ...item, ...next } : item
      )));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to update oversight flag.');
    } finally {
      setSavingFlag(false);
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Card>
          <CardHeader
            action={<IconButton onClick={loadConversations} disabled={loadingList}>{loadingList ? <CircularProgress size={18} /> : <RefreshRoundedIcon />}</IconButton>}
          />
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  placeholder="Search parent, tutor, booking number, subject, or message preview"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') loadConversations(); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Switch checked={flaggedOnly} onChange={(event) => setFlaggedOnly(event.target.checked)} />
                  <Typography variant="body2">Flagged only</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button fullWidth variant="contained" onClick={loadConversations}>Search</Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ height: { lg: 'calc(100vh - 300px)', xs: 'auto' }, minHeight: { lg: 540, xs: 0 } }}>
          <Card sx={{ width: { xs: '100%', lg: 390 }, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {loadingList && <LinearProgress />}
            <CardHeader title="Conversations" subheader={`${filteredConversations.length} loaded`} />
            <Divider />
            <CardContent sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.5 }}>
              <Stack spacing={1}>
                {filteredConversations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No conversations found.</Typography>
                ) : filteredConversations.map((item) => {
                  const active = selected?.conversationKey === item.conversationKey;
                  return (
                    <Paper
                      key={item.conversationKey}
                      component="button"
                      type="button"
                      onClick={() => setSelected(item)}
                      elevation={0}
                      sx={{
                        width: '100%',
                        textAlign: 'left',
                        border: '1px solid',
                        borderColor: active ? 'success.main' : 'divider',
                        bgcolor: active ? 'success.light' : 'background.paper',
                        borderRadius: 2.5,
                        p: 1.5,
                        cursor: 'pointer',
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            {item.parent?.name || 'Parent'} ↔ {item.lmsUser?.name || 'Tutor'}
                          </Typography>
                          {item.flagged && <FlagRoundedIcon fontSize="small" color="error" />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {item.booking?.bookingNumber || 'No booking linked'} · {item.booking?.subject || 'Subject not listed'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{preview(item.latestMessagePreview)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.messageCount} messages · {formatDateTime(item.latestMessageAt)}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: selected ? 1 : { xs: '0 0 auto', lg: 1 }, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title={selected ? `${selected.parent?.name || 'Parent'} and ${selected.lmsUser?.name || 'Tutor'}` : 'Select a conversation'}
              subheader={selected ? `${selected.booking?.bookingNumber || 'No booking linked'} · ${selected.booking?.subject || 'Subject not listed'} · ${selected.messageCount || 0} messages` : 'Read-only transcript appears here.'}
              action={selected ? (
                <Chip
                  icon={<VisibilityRoundedIcon />}
                  label="Read-only"
                  color="info"
                  size="small"
                />
              ) : null}
            />
            <Divider />
            {selected && (
              <>
                <CardContent sx={{ py: 1.5 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Internal admin note"
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        placeholder="Add internal follow-up notes. Not visible to parent or tutor."
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                        <Button variant={selected.flagged ? 'contained' : 'outlined'} color="error" disabled={savingFlag} onClick={() => saveFlag(!selected.flagged)} startIcon={<FlagRoundedIcon />}>
                          {selected.flagged ? 'Unflag' : 'Flag'}
                        </Button>
                        <Button variant="outlined" disabled={savingFlag} onClick={() => saveFlag(Boolean(selected.flagged))}>
                          Save Note
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
                <Divider />
              </>
            )}
            <CardContent sx={{ flex: selected ? 1 : 0, minHeight: 0, overflowY: 'auto', bgcolor: 'grey.50' }}>
              {loadingMessages ? (
                <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" color="text.secondary">Loading transcript...</Typography>
                </Stack>
              ) : !selected ? (
                <Typography variant="body2" color="text.secondary">Choose a conversation from the left.</Typography>
              ) : messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No messages found for this conversation.</Typography>
              ) : (
                <Stack spacing={1.5}>
                  {messages.map((message) => <MessageBubble key={message._id} message={message} />)}
                </Stack>
              )}
            </CardContent>
            {selected ? (
              <>
                <Divider />
                <CardContent>
                  <Alert severity="info">
                    Oversight is read-only. To intervene, use the existing Admin Messaging page and contact the parent or tutor through Apex Support.
                  </Alert>
                </CardContent>
              </>
            ) : null}
          </Card>
        </Stack>
      </Stack>
    </DashboardLayout>
  );
};

export default MessageOversight;
